import {useEffect, useRef} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {graphqlClient} from '@/services/graphqlClient';
import {
  MedicationForm,
  useGetMedicinalProductQuery,
} from '@/services/graphqlHooks';
import medicationForm from '@/translation/medicationForm';
import {localeCompareSort} from '@/utils/localeCompareSort';

import type {ResidentMedicationBasicFormValues} from '../../utils/medicationFormInputs';
import {parsePharmaceuticalForm} from '../../utils/parsePharmaceuticalForm';
import LinkedMedicinalProduct from '../LinkedMedicinalProduct';
import MedicationInput from '../MedicationInput';

export type MedicationIdentityFieldsProps = {
  form: UseFormReturn<ResidentMedicationBasicFormValues>;
};

const medicationFormOptions = Object.values(MedicationForm).sort((a, b) =>
  localeCompareSort(medicationForm[a], medicationForm[b])
);

function MedicationIdentityFields({form}: MedicationIdentityFieldsProps) {
  const {t} = useTranslation();

  const productId = useWatch({
    control: form.control,
    name: 'productId',
  });

  const pendingAutofillRef = useRef(false);

  const {data: productData} = useGetMedicinalProductQuery(
    graphqlClient,
    {productId: productId ?? ''},
    {enabled: !!productId}
  );

  useEffect(() => {
    if (!pendingAutofillRef.current) return;
    const pharmaceuticalForm =
      productData?.getMedicinalProduct?.pharmaceuticalForm;
    if (!pharmaceuticalForm) return;

    pendingAutofillRef.current = false;
    const parsed = parsePharmaceuticalForm(pharmaceuticalForm);
    if (parsed) {
      form.setValue('form', parsed, {shouldDirty: true});
    }
  }, [productData, form]);

  return (
    <>
      <FormField
        control={form.control}
        name="medicationName"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Medication name')}</FormLabel>
            <FormControl>
              <MedicationInput
                value={field.value}
                onChange={field.onChange}
                onProductIdChange={newProductId => {
                  if (newProductId) pendingAutofillRef.current = true;
                  form.setValue('productId', newProductId, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                initialProductId={productId}
              />
            </FormControl>
            <FormField
              control={form.control}
              name="productId"
              render={({field}) => (
                <input
                  ref={field.ref}
                  type="hidden"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              )}
            />
            {productId && (
              <LinkedMedicinalProduct
                productId={productId}
                onRemove={() => {
                  form.setValue('productId', undefined, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="form"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Medication form')}</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Select form')} />
                </SelectTrigger>
                <SelectContent>
                  {medicationFormOptions.map(form => (
                    <SelectItem key={form} value={form}>
                      {medicationForm[form]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

export default MedicationIdentityFields;
